import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import TeacherMenu from "./TeacherMenu";
import config from "../../Config";

function TeacherTimeTable() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);
  const [MemberType] = useState(localStorage.getItem("MemberType") || "Teacher");
  const [SessionID] = useState(parseInt(localStorage.getItem("DefaultSession"), 10) || 0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [monthly, setMonthly] = useState({ TotalPeriods: 0, TotalMinutes: 0, TotalHours: 0 });
  const [teacherName] = useState(localStorage.getItem("FullName") || localStorage.getItem("UserName") || "Teacher");

  useEffect(() => {
    document.title = "Teaching Time Table";
    axios({
      method: "post",
      url: config.base_url + "Teacher/GetMyTeachingTimeTable",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, SessionID },
    })
      .then((res) => {
        if (res.data.status_code === 1) setRows(res.data.ListofRecords || []);
        else setRows([]);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));

    axios({
      method: "post",
      url: config.base_url + "Teacher/GetMyMonthlyTeachingLoad",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, SessionID },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setMonthly({
            TotalPeriods: res.data.TotalPeriods || 0,
            TotalMinutes: res.data.TotalMinutes || 0,
            TotalHours: res.data.TotalHours || 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const days = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.Day_ID]) map[r.Day_ID] = r.Day_Name || `Day ${r.Day_ID}`;
    });
    return Object.keys(map).map((k) => ({ id: parseInt(k, 10), name: map[k] })).sort((a, b) => a.id - b.id);
  }, [rows]);

  const periods = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.Period_ID]) map[r.Period_ID] = r.Period_Name || `Period ${r.Period_ID}`;
    });
    return Object.keys(map).map((k) => ({ id: parseInt(k, 10), name: map[k] })).sort((a, b) => a.id - b.id);
  }, [rows]);

  const cellText = (dayId, periodId) => {
    const row = rows.find((x) => x.Day_ID === dayId && x.Period_ID === periodId);
    if (!row) return "";
    if (row.Is_Break) return "Break";
    if (row.Is_FreePeriod) return "Free Period";
    return row.Subject_Name || "";
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const headers = ["Time", ...days.map((d) => d.name)];
    const lines = [headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(",")];
    periods.forEach((p) => {
      const row = [p.name, ...days.map((d) => cellText(d.id, p.id))];
      lines.push(row.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "teacher-timetable.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shellStyle = {
    background: "linear-gradient(120deg, #0f4cad 0%, #8b4cd8 100%)",
    borderRadius: 14,
    padding: 18,
  };
  const timeHeadStyle = {
    background: "#58d5df",
    color: "#0b1330",
    fontWeight: 700,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
    minWidth: 140,
  };
  const dayHeadStyle = {
    background: "#f7ced5",
    color: "#1f2440",
    fontWeight: 700,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
    minWidth: 170,
  };
  const timeCellStyle = {
    background: "#73d8df",
    color: "#10183d",
    fontWeight: 600,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
  };
  const slotCellStyle = {
    background: "#f3f4f6",
    color: "#1c2648",
    border: "6px solid transparent",
    borderRadius: 8,
    minHeight: 62,
    verticalAlign: "middle",
    fontSize: 14,
  };

  return (
    <>
      <TeacherMenu />
      <div className="body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><NavLink to="/teacher/dashboard">Home</NavLink></li>
                    <li className="breadcrumb-item active">Class Time Table</li>
                  </ol>
                </nav>
                <div className="page-title">Class Time Table</div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-content">
          <div className="container">
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4"><b>Monthly Periods:</b> {monthly.TotalPeriods}</div>
                  <div className="col-md-4"><b>Monthly Minutes:</b> {monthly.TotalMinutes}</div>
                  <div className="col-md-4"><b>Monthly Hours:</b> {monthly.TotalHours}</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <Spinner animation="border" />
                ) : rows.length === 0 ? (
                  <p className="text-muted mb-0">No active teaching timetable available.</p>
                ) : (
                  <div style={shellStyle}>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-7">
                        <h2 style={{ color: "#ffffff", fontWeight: 800, marginBottom: 0 }}>Class Schedule</h2>
                      </div>
                      <div className="col-md-5">
                        <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ fontWeight: 700, color: "#111827" }}>Name : {teacherName}</div>
                          <div style={{ fontWeight: 700, color: "#111827" }}>Subject : Teaching Time Table</div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      <button type="button" className="btn btn-light btn-sm" onClick={() => window.print()}>Print</button>
                      <button type="button" className="btn btn-light btn-sm" onClick={exportCsv}>Export CSV</button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-borderless table-sm mb-0">
                        <thead>
                          <tr>
                            <th style={timeHeadStyle}>TIME</th>
                            {days.map((d) => <th key={d.id} style={dayHeadStyle}>{String(d.name || "").toUpperCase()}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {periods.map((p) => (
                            <tr key={p.id}>
                              <td style={timeCellStyle}>{p.name}</td>
                              {days.map((d) => (
                                <td key={`${d.id}_${p.id}`} style={slotCellStyle}>{cellText(d.id, p.id)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TeacherTimeTable;
